/**
 * Referral CSV Export Utilities
 * Handles conversion of referral data to CSV format
 */

import { convertToCSV, formatDateForCsv, downloadCSV } from '../utils/csvUtils';
import { getReferralTypeLabel } from './ReferralModal';

/**
 * Format NHS ID with spaces (XXX XXX XXXX)
 * @param {string} nhsId - NHS ID string
 * @returns {string} Formatted NHS ID
 */
const formatNhsId = (nhsId) => {
  if (!nhsId || nhsId.length !== 10) return nhsId || '';
  return `${nhsId.slice(0, 3)} ${nhsId.slice(3, 6)} ${nhsId.slice(6)}`;
};

/**
 * Column configuration for referral CSV export
 */
const REFERRAL_CSV_COLUMNS = [
  {
    key: 'patientName',
    header: 'Patient Name',
    formatter: (referral) => referral.patient?.name || ''
  },
  {
    key: 'nhsId',
    header: 'NHS Number',
    formatter: (referral) => formatNhsId(referral.patient?.nhsId)
  },
  {
    key: 'referralType',
    header: 'Referral Type',
    formatter: (referral) => getReferralTypeLabel(referral.referralType)
  },
  {
    key: 'referredAt',
    header: 'Date of Referral',
    formatter: (referral) => formatDateForCsv(referral.referredAt)
  },
  {
    key: 'status',
    header: 'Status',
    formatter: (referral) => referral.isComplete ? 'COMPLETE' : 'PENDING'
  },
  {
    key: 'completedAt',
    header: 'Date Completed',
    formatter: (referral) => formatDateForCsv(referral.completedAt)
  },
  {
    key: 'text',
    header: 'Notes',
    formatter: (referral) => referral.text || ''
  }
];

/**
 * Convert referrals to CSV string
 * @param {Array} referrals - Array of referral objects
 * @returns {string} CSV string
 */
export const convertReferralsToCSV = (referrals) => {
  return convertToCSV(referrals, REFERRAL_CSV_COLUMNS);
};

/**
 * Download referrals as CSV file
 * @param {Array} referrals - Array of referral objects
 * @param {string} [filename] - Optional custom filename
 */
export const downloadReferralsCSV = (referrals, filename) => {
  const csvContent = convertReferralsToCSV(referrals);
  const timestamp = new Date().toISOString();
  const actualFilename = filename || `referrals_export_${timestamp}.csv`;
  downloadCSV(csvContent, actualFilename);
};

/**
 * Fetch and download all referrals (active + archive) sorted by date
 * @param {Object} referralManager - ReferralService instance
 * @returns {Promise<number>} Number of referrals downloaded
 */
export const downloadAllReferrals = async (referralManager) => {
  const [activeReferrals, archiveReferrals] = await Promise.all([
    referralManager.fetchActiveReferrals(),
    referralManager.fetchArchiveReferrals()
  ]);

  const allReferrals = [...activeReferrals, ...archiveReferrals];
  
  // Sort by referredAt date ascending (oldest first)
  allReferrals.sort((a, b) => {
    const dateA = a.referredAt ? new Date(a.referredAt).getTime() : 0;
    const dateB = b.referredAt ? new Date(b.referredAt).getTime() : 0;
    return dateA - dateB;
  });

  downloadReferralsCSV(allReferrals);
  
  return allReferrals.length;
};
